import React from 'react';
import { format, isToday, isYesterday } from 'date-fns';
import { es } from 'date-fns/locale';

const ConversationList = ({ conversations = [], activeConversationId, onSelectConversation, currentUserId }) => {
  const formatLastMessageTime = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'HH:mm', { locale: es });
    }
    if (isYesterday(date)) {
      return 'Ayer';
    }
    return format(date, 'dd/MM/yy', { locale: es });
  };

  const truncateMessage = (text, maxLength = 50) => {
    if (!text) return 'No hay mensajes todavía';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (conversations.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 p-8">
        <div className="text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
          </svg>
          <p className="text-sm font-medium">No tienes conversaciones</p>
          <p className="text-xs mt-1">Inicia una nueva conversación para empezar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
      {conversations.map((conversation) => {
        const isActive = conversation.id === activeConversationId;
        const hasUnread = conversation.unread_count > 0;

        return (
          <div
            key={conversation.id}
            onClick={() => onSelectConversation(conversation)}
            className={`
              flex items-center px-4 py-3 cursor-pointer transition-colors border-l-4
              ${isActive 
                ? 'bg-gradient-to-r from-cyan-900/30 to-teal-900/30 border-cyan-500' 
                : 'bg-gray-900 border-transparent hover:bg-gray-800'
              }
            `}
          >
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              <div className={`
                w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg
                ${hasUnread ? 'ring-2 ring-cyan-500' : ''}
              `}
              style={{ 
                backgroundColor: conversation.participant_avatar_color || '#6366f1'
              }}
              >
                {conversation.participant_avatar_url ? (
                  <img
                    src={conversation.participant_avatar_url}
                    alt={conversation.participant_name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span>
                    {conversation.participant_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              
              {/* Online indicator (optional) */}
              {conversation.participant_online && (
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 ml-3 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className={`text-sm font-semibold truncate ${hasUnread ? 'text-cyan-400' : 'text-gray-300'}`}>
                  {conversation.participant_name || 'Usuario'}
                </h3>
                
                {conversation.last_message_at && (
                  <span className={`text-xs ml-2 flex-shrink-0 ${hasUnread ? 'text-cyan-400 font-medium' : 'text-gray-500'}`}>
                    {formatLastMessageTime(conversation.last_message_at)}
                  </span>
                )}
              </div>
              
              <div className="flex items-center justify-between">
                <p className={`text-sm truncate ${hasUnread ? 'text-gray-200 font-medium' : 'text-gray-400'}`}>
                  {truncateMessage(conversation.last_message)}
                </p>
                
                {hasUnread && (
                  <span className="ml-2 flex-shrink-0 bg-gradient-to-r from-cyan-600 to-teal-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                  </span>
                )}
              </div>
              
              {/* Participant type badge (mentor/student) */}
              {conversation.participant_type && (
                <div className="mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    conversation.participant_type === 'mentor' 
                      ? 'bg-purple-900/50 text-purple-300'
                      : 'bg-cyan-900/50 text-cyan-300'
                  }`}>
                    {conversation.participant_type === 'mentor' ? '👨‍🏫 Mentor' : '👤 Estudiante'}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ConversationList;
